import React, { useMemo } from 'react';
import { Combobox, Field } from '@repo/design-system';
import { countryZones } from '@repo/front-end-utils';

/**
 * The timezone picker — ONE component, opened by *Change* on the create route's timezone card and
 * shown by the summary's Timezone card in edit (`twin-details-page-summary`, R5.3: lifted out of
 * `TwinDetailsDerived`, never copied). What a choice does is the caller's: the create route saves
 * it, the summary asks first (R5.5).
 */

/** Every canonical zone, read once. `[]` on an engine without `supportedValuesOf`. */
const CANONICAL_ZONES: string[] = (() => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return [];
  }
})();

/**
 * The picker's list (`twin-details-page-address`, R4.6): every canonical zone, plus the stored one
 * when it is not among them (`UTC` and legacy aliases are not canonical, and a `Combobox` would
 * otherwise show it as text it cannot find), the country's own zones first. Never the browser's
 * zone (R4.7).
 */
export const changeZoneOptions = (
  countryCode: string | null,
  countryLabel: string | null,
  stored: string,
): Array<{ value: string; label: string; group?: string }> => {
  const own = (countryCode && countryZones(countryCode)) || [];
  const all = [...CANONICAL_ZONES];
  const kept = stored.trim();
  if (kept && !all.includes(kept)) all.push(kept);
  const first = own.map(zone => ({ value: zone, label: zone, group: `In ${countryLabel}` }));
  const rest = all
    .filter(zone => !own.includes(zone))
    .map(zone => ({ value: zone, label: zone, group: own.length ? 'Every timezone' : undefined }));
  return [...first, ...rest];
};

interface Props {
  countryCode: string | null;
  countryLabel: string | null;
  /** The twin's stored zone, shown as the picker's value. */
  stored: string;
  /** A committed choice, or `null` when cleared. */
  onChoose: (zone: string | null) => void;
}

const ZonePicker: React.FC<Props> = ({ countryCode, countryLabel, stored, onChoose }) => {
  const kept = stored.trim();
  const options = useMemo(
    () => changeZoneOptions(countryCode, countryLabel, kept),
    [countryCode, countryLabel, kept],
  );
  return (
    <Field label="Timezone">
      {wiring => (
        <Combobox
          {...wiring}
          options={options}
          value={kept || undefined}
          placeholder="Search timezones"
          maxResults={Infinity}
          emptyText="No timezone by that name"
          commitOnClose={false}
          onChange={zone => onChoose(zone)}
        />
      )}
    </Field>
  );
};

export default ZonePicker;
